/* =============================================================================
 * AudienceSwitch: "Athletes & families | College coaches" on /login and /signup
 * -----------------------------------------------------------------------------
 * Two links that flip the page you are on between family and coach mode by
 * setting or clearing ?coach=1. Links, not tabs: each mode is its own URL.
 * ========================================================================== */
import React from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { font, radius } from '../styles/tokens';

const OPTIONS = [
  { coach: false, label: 'Athletes & families' },
  { coach: true, label: 'College coaches' },
] as const;

export const AudienceSwitch: React.FC<{ coach: boolean }> = ({ coach }) => {
  const { pathname } = useLocation();
  const [params] = useSearchParams();

  const hrefFor = (toCoach: boolean) => {
    const next = new URLSearchParams(params);
    if (toCoach) next.set('coach', '1');
    else next.delete('coach');
    // An invite token belongs to one audience. Carried across, a coach invite
    // would be read as a family invitation, and the other way round.
    if (toCoach !== coach) next.delete('invite');
    const query = next.toString();
    return query ? `${pathname}?${query}` : pathname;
  };

  return (
    <nav
      aria-label="Account type"
      style={{
        display: 'flex',
        gap: 4,
        padding: 4,
        marginBottom: 24,
        background: 'var(--aps-fill)',
        borderRadius: radius.lg,
      }}
    >
      {OPTIONS.map((option) => {
        const active = option.coach === coach;
        return (
          <Link
            key={option.label}
            to={hrefFor(option.coach)}
            replace
            aria-current={active ? 'page' : undefined}
            style={{
              flex: 1,
              minHeight: 44,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '8px 12px',
              borderRadius: radius.md,
              fontFamily: font.sans,
              fontSize: 14,
              fontWeight: 700,
              textAlign: 'center',
              textDecoration: 'none',
              background: active ? 'var(--aps-ink)' : 'transparent',
              color: active ? 'var(--aps-on-ink)' : 'var(--aps-text-2)',
              transition: 'background var(--aps-dur-fast) var(--aps-ease-out)',
            }}
          >
            {option.label}
          </Link>
        );
      })}
    </nav>
  );
};

export default AudienceSwitch;
